/**
 * Tenant-link invite flow — client-side token stash (tenant side, Maciek's domain).
 *
 * Extracted from three inline `localStorage.getItem('fh_invite_token')` call
 * sites (App.tsx / AuthPage.tsx / BasicInfoStep.tsx) so the invite token gets the
 * same lifetime as the claim + seeker tokens. Before this, the raw value was
 * stashed with no validation and no expiry, so a token picked up months earlier
 * still reshaped a later, unrelated signup — the same class of defect that let a
 * stale claim token hand somebody an owner account (2026-07-30).
 *
 * A dropped invite token degrades gracefully: the server-side front-door match
 * (`match-tenant-link` mode `lookup_by_email`) still links the invite by the
 * account's own email, so the invitee is not stranded.
 */

import { clearFlowToken, readFlowToken, stashFlowToken } from '@/lib/flowToken';

const INVITE_TOKEN_KEY = 'fh_invite_token';

/** Invite tokens are UUIDs (`crypto.randomUUID()` in send-tenant-link-invite). */
export function isInviteToken(token: string | null | undefined): token is string {
  return !!token && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(token);
}

/** Bounded by FLOW_TOKEN_TTL_MS — see flowToken.ts. Restarts the clock. */
export function stashInviteToken(token: string): void {
  if (isInviteToken(token)) stashFlowToken(INVITE_TOKEN_KEY, token);
}

export function getPendingInviteToken(): string | null {
  return readFlowToken(INVITE_TOKEN_KEY, isInviteToken);
}

export function clearPendingInviteToken(): void {
  clearFlowToken(INVITE_TOKEN_KEY);
}
